import { useEffect } from 'react';
import {
  DEFAULT_V5_COLOR_MODE,
  DEFAULT_V5_DENSITY,
  type V5ColorMode,
  type V5DensityMode,
} from '@routific/hive/v5';
import { useStore } from '../store/mst';

/**
 * Resolves the active Hive V5 theme. The hidden, session-only dark-mode override
 * (see useV5DarkModeShortcut) wins; otherwise Routicians get their internal-tools
 * selection and everyone else the product defaults (light / md). Call from an
 * `observer` component so it reacts to changes.
 *
 * Reads are optional-chained so a partially-populated store (e.g. test mocks or
 * early boot before the account/internal-tools slices hydrate) falls back to the
 * product defaults instead of throwing.
 */
export function useResolvedV5Theme(): { colorMode: V5ColorMode; density: V5DensityMode } {
  const { account, internalToolsUI } = useStore();

  const routicianColorMode = account?.isRoutician
    ? internalToolsUI?.effectiveColorMode ?? DEFAULT_V5_COLOR_MODE
    : DEFAULT_V5_COLOR_MODE;

  const colorMode: V5ColorMode = internalToolsUI?.sessionDarkMode ? 'dark' : routicianColorMode;

  const density: V5DensityMode = account?.isRoutician
    ? internalToolsUI?.effectiveDensity ?? DEFAULT_V5_DENSITY
    : DEFAULT_V5_DENSITY;

  return { colorMode, density };
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  if (!element) return false;
  const tag = element.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable;
};

/**
 * Hidden shortcut: Shift+D toggles a session-only V5 dark mode across the app's
 * V5 surfaces. Ignored while typing in a field (or when another modifier is held)
 * so it never interferes with normal input.
 */
export function useV5DarkModeShortcut(): void {
  const { internalToolsUI } = useStore();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.repeat) return;
      if (event.key.toLowerCase() !== 'd') return;
      if (isEditableTarget(event.target)) return;

      event.preventDefault();
      internalToolsUI.toggleSessionDarkMode();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [internalToolsUI]);
}
